import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import {WithId} from '../../../shared/util';
import {TestList} from '../../../shared/testLists';
import {
  useDeleteTestList,
  useGetTestListReferences,
} from '../../../hooks/api/testList';
import {Bold} from '../../text';

export const DeleteTestListModal = ({
  testList,
  isOpen,
  onClose,
}: {
  testList: WithId<TestList>;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW="lg">
        <DeleteTestListModalContent testList={testList} onClose={onClose} />
      </ModalContent>
    </Modal>
  );
};

const DeleteTestListModalContent = ({
  testList,
  onClose,
}: {
  testList: WithId<TestList>;
  onClose: () => void;
}) => {
  const deleteTestList = useDeleteTestList();
  const testListReferences = useGetTestListReferences({
    testListId: testList._id,
  });

  const handleConfirmDelete = () => {
    deleteTestList.mutate({testListId: testList._id}, {onSuccess: onClose});
  };

  const hasTestListReferences =
    testListReferences.data &&
    (testListReferences.data.agents.length > 0 ||
      testListReferences.data.markets.length > 0);

  return (
    <>
      <ModalHeader>
        <Text>Delete Test List</Text>
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Flex direction="column" gap={4}>
          <Box
            bg="kred.50"
            borderWidth="1px"
            borderColor="kred.100"
            p={4}
            rounded="md"
          >
            <Text>
              Are you sure you want to delete <Bold>{testList.label}</Bold>?{' '}
              This action cannot be undone.
            </Text>
          </Box>
          {testListReferences.isPending ? (
            <Stack gap={2}>
              <Skeleton height="20px" />
              <Skeleton height="20px" />
            </Stack>
          ) : testListReferences.isError ? (
            <Alert status="error">
              <AlertIcon />
              Error loading test list references. Please reload the page or
              contact us for help.
            </Alert>
          ) : (
            hasTestListReferences && (
              <Flex direction="column" gap={3}>
                <Alert status="warning" variant="subtle" rounded="md">
                  <AlertIcon />
                  This test list will be removed from the following:
                </Alert>
                {testListReferences.data.agents.length > 0 && (
                  <Box p={3}>
                    <HStack mb={2}>
                      <Text fontWeight="medium">Agents</Text>
                      <Badge>{testListReferences.data.agents.length}</Badge>
                    </HStack>
                    <UnorderedList pl={5} spacing={1}>
                      {testListReferences.data.agents.map(({id, label}) => (
                        <ListItem key={id.toString()}>{label}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                )}
                {testListReferences.data.markets.length > 0 && (
                  <Box p={3}>
                    <HStack mb={2}>
                      <Text fontWeight="medium">Markets</Text>
                      <Badge>{testListReferences.data.markets.length}</Badge>
                    </HStack>
                    <UnorderedList pl={5} spacing={1}>
                      {testListReferences.data.markets.map(({id, label}) => (
                        <ListItem key={id.toString()}>{label}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                )}
              </Flex>
            )
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          mr={3}
          variant="ghost"
          onClick={onClose}
          isDisabled={deleteTestList.isPending}
        >
          Cancel
        </Button>
        <Button
          colorScheme="red"
          onClick={handleConfirmDelete}
          isDisabled={
            testListReferences.isPending || testListReferences.isError
          }
          isLoading={deleteTestList.isPending}
        >
          Delete
        </Button>
      </ModalFooter>
    </>
  );
};
